import { useMemo, useState } from 'react';

import { useGetUsersQuery, useInviteUsersToTenantMutation } from '@features/users';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { logger } from '@/utils/logger';

interface InviteUsersModalProps {
  tenantId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

interface InviteUser {
  email: string;
  isAdmin: boolean;
}

// Email regex validation - stricter, more realistic
// Allows: letters, numbers, . _ % + - before @
// Requires valid domain with TLD (at least 2 letters)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Invite Users Modal
 * Allows admin to invite multiple users to tenant with role selection
 */
export const InviteUsersModal = ({ tenantId, isOpen, setIsOpen }: InviteUsersModalProps) => {
  const [emailInput, setEmailInput] = useState('');
  const [inviteList, setInviteList] = useState<InviteUser[]>([]);
  const [error, setError] = useState('');

  const { data: allUsers = [] } = useGetUsersQuery();
  const [inviteUsers, { isLoading: isInviting }] = useInviteUsersToTenantMutation();

  // Filter users by current tenant
  const tenantUsers = allUsers.filter((u) => u.tenantid === tenantId);

  // Real-time email validation - memoized to ensure proper reactivity
  const realTimeError = useMemo(() => {
    logger.log('🔍 Validating email:', emailInput); // DEBUG
    if (!emailInput) {
      return '';
    }

    // Check email format
    if (!EMAIL_REGEX.test(emailInput)) {
      logger.log('❌ Failed EMAIL_REGEX test'); // DEBUG
      return 'Please enter a valid email address';
    }

    // Check if already in invite list
    if (inviteList.find((u) => u.email.toLowerCase() === emailInput.toLowerCase())) {
      logger.log('❌ Already in invite list'); // DEBUG
      return 'You have already added this email to the invite list';
    }

    // Check if user already exists in organization
    if (tenantUsers.find((u) => u.username.toLowerCase() === emailInput.toLowerCase())) {
      logger.log('❌ Already a member'); // DEBUG
      return 'The user is already a member of this Organization';
    }

    logger.log('✅ Email is valid'); // DEBUG
    return '';
  }, [emailInput, inviteList, tenantUsers]);

  const handleClose = () => {
    setIsOpen(false);
    setEmailInput('');
    setInviteList([]);
    setError('');
  };

  const addEmail = () => {
    setError('');

    // Validate email format
    if (!EMAIL_REGEX.test(emailInput)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if already in invite list
    if (inviteList.find((u) => u.email.toLowerCase() === emailInput.toLowerCase())) {
      setError('You have already added this email to the invite list');
      return;
    }

    // Check if user already exists in organization
    if (tenantUsers.find((u) => u.username.toLowerCase() === emailInput.toLowerCase())) {
      setError('The user is already a member of this Organization');
      return;
    }

    // Add to invite list
    setInviteList([...inviteList, { email: emailInput, isAdmin: false }]);
    setEmailInput('');
  };

  const removeEmail = (email: string) => {
    setInviteList(inviteList.filter((u) => u.email !== email));
  };

  const toggleAdmin = (email: string) => {
    setInviteList(inviteList.map((u) => (u.email === email ? { ...u, isAdmin: !u.isAdmin } : u)));
  };

  const handleInvite = async () => {
    if (inviteList.length === 0) {
      return;
    }

    try {
      await inviteUsers({
        tenantId,
        users: inviteList.map((u) => ({ email: u.email, isAdmin: u.isAdmin })),
      }).unwrap();
      handleClose();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { message?: string } };
      logger.error('Failed to invite users:', err);

      // Handle specific error cases
      if (apiErr?.status === 403) {
        alert(
          "You don't have permission to invite users to this organization.\n\n" +
            'Only organization administrators can invite new members.'
        );
      } else if (apiErr?.data?.message) {
        alert(`Failed to send invitations: ${apiErr.data.message}`);
      } else {
        alert('Failed to send invitations. Please try again.');
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_event, reason) => {
        // Only close when button is clicked, not on backdrop click or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableScrollLock
      disableEscapeKeyDown
    >
      <DialogTitle>
        <strong>Invite users to this Organization</strong>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Role Info */}
          {inviteList.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                <strong>About access roles:</strong>
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                <strong>Admin:</strong> Highest level of access. Can view/invite/edit/remove other
                users.
              </Typography>
              <Typography variant="caption" component="div">
                <strong>User:</strong> Lowest level of access. Can only view users and invite others
                with user role.
              </Typography>
            </Alert>
          )}

          {/* Invite List */}
          {inviteList.length > 0 && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Users to invite:</strong>
              </Typography>
              <List dense sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                {inviteList.map((user) => (
                  <ListItem
                    key={user.email}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption">
                            {user.isAdmin ? 'Admin' : 'User'}
                          </Typography>
                          <Switch
                            checked={user.isAdmin}
                            onChange={() => toggleAdmin(user.email)}
                            size="small"
                          />
                        </Box>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeEmail(user.email)}
                          title="Remove"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={user.email} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider />

          {/* Email Input */}
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>Invite new members by Email:</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                type="email"
                placeholder="Enter email address"
                value={emailInput}
                onChange={(e) => {
                  const newEmail = e.target.value.replace(/ /g, '').toLowerCase();
                  setEmailInput(newEmail);
                  setError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                error={!!realTimeError}
                helperText={realTimeError || ' '}
                fullWidth
                size="small"
                slotProps={{
                  formHelperText: {
                    sx: { minHeight: '20px', margin: '4px 0 0 0' },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={addEmail}
                disabled={!emailInput || !!realTimeError}
                sx={{ minWidth: 80 }}
              >
                Add
              </Button>
            </Box>
            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleClose} disabled={isInviting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleInvite}
          disabled={isInviting || inviteList.length === 0}
        >
          {isInviting ? <CircularProgress size={20} /> : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
